import type { Types as CoreTypes } from '@cornerstonejs/core';
import { annotation as csAnnotation } from '@cornerstonejs/tools';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/models/spectralDoppler';
import { distanceToSegment } from './SpectralDoppler/geometry';

const LABEL_OFFSET_PX = 28;
const VIEWPORT_EDGE_MARGIN_PX = 60;
// A candidate this close (canvas px) to another label's anchor is penalized —
// roughly one 2-line doppler label's footprint, so clustered E/A probe + VTI
// labels fan out to different sides instead of stacking (AP-616).
const LABEL_AVOID_RADIUS_PX = 90;

interface ViewportSize {
  width: number;
  height: number;
}

interface PickAnchorOptions {
  /**
   * Bias the score so anchors above the geometry are preferred. Useful for
   * VTI traces (envelope is below baseline → label naturally goes above).
   */
  preferAbove?: boolean;
  /** Prefer anchors below the geometry, e.g. volume/area labels off the contour. */
  preferBelow?: boolean;
  /**
   * Canvas-space anchors of OTHER labels to steer clear of. Candidates within
   * LABEL_AVOID_RADIUS_PX of any are penalized so labels don't overlap.
   */
  avoidCanvasPoints?: CoreTypes.Point2[];
}

/**
 * Pick a label anchor in canvas-space that doesn't overlap the geometry and
 * stays inside the viewport. Returns the original geometry centroid as a
 * graceful fallback when geometryCanvasPoints is empty.
 *
 * Algorithm: enumerate 8 candidates around the geometry's bounding box,
 * score each by (distance to nearest geometry segment) − (viewport-edge
 * penalty), and return the winner. Deterministic.
 */
export function pickLabelAnchor(
  geometryCanvasPoints: CoreTypes.Point2[],
  viewportSize: ViewportSize,
  options?: PickAnchorOptions
): CoreTypes.Point2 {
  if (geometryCanvasPoints.length === 0) {
    return [viewportSize.width / 2, viewportSize.height / 2];
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < geometryCanvasPoints.length; i += 1) {
    const [x, y] = geometryCanvasPoints[i];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  const offset = LABEL_OFFSET_PX;
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const candidates: CoreTypes.Point2[] = [
    [maxX + offset, minY], // top-right
    [minX - offset, minY], // top-left
    [maxX + offset, maxY], // bottom-right
    [minX - offset, maxY], // bottom-left
    [maxX + offset, midY], // middle-right
    [minX - offset, midY], // middle-left
    [midX, minY - offset], // top-middle
    [midX, maxY + offset], // bottom-middle
  ];

  let bestScore = -Infinity;
  let bestAnchor: CoreTypes.Point2 = candidates[0];

  for (let c = 0; c < candidates.length; c += 1) {
    const cand = candidates[c];

    let minDist = Infinity;
    if (geometryCanvasPoints.length === 1) {
      const p = geometryCanvasPoints[0];
      minDist = Math.hypot(cand[0] - p[0], cand[1] - p[1]);
    } else {
      for (let i = 1; i < geometryCanvasPoints.length; i += 1) {
        const d = distanceToSegment(
          geometryCanvasPoints[i - 1],
          geometryCanvasPoints[i],
          cand
        );
        if (d < minDist) minDist = d;
      }
    }

    let edgePenalty = 0;
    if (cand[0] < VIEWPORT_EDGE_MARGIN_PX) {
      edgePenalty += VIEWPORT_EDGE_MARGIN_PX - cand[0];
    }
    if (cand[0] > viewportSize.width - VIEWPORT_EDGE_MARGIN_PX) {
      edgePenalty += cand[0] - (viewportSize.width - VIEWPORT_EDGE_MARGIN_PX);
    }
    if (cand[1] < VIEWPORT_EDGE_MARGIN_PX) {
      edgePenalty += VIEWPORT_EDGE_MARGIN_PX - cand[1];
    }
    if (cand[1] > viewportSize.height - VIEWPORT_EDGE_MARGIN_PX) {
      edgePenalty += cand[1] - (viewportSize.height - VIEWPORT_EDGE_MARGIN_PX);
    }

    let preferenceBias = 0;
    if (options?.preferAbove && cand[1] < midY) {
      preferenceBias = 10;
    }
    if (options?.preferBelow && cand[1] > midY) {
      preferenceBias = 10;
    }

    // Repel from other labels' anchors. Linear falloff to 0 at the radius, so
    // a candidate sitting on another label costs the full radius (dominates
    // the ~28px minDist spread) but distant candidates are unaffected.
    let avoidPenalty = 0;
    const avoid = options?.avoidCanvasPoints;
    if (avoid) {
      for (let a = 0; a < avoid.length; a += 1) {
        const d = Math.hypot(cand[0] - avoid[a][0], cand[1] - avoid[a][1]);
        if (d < LABEL_AVOID_RADIUS_PX)
          avoidPenalty += LABEL_AVOID_RADIUS_PX - d;
      }
    }

    const score = minDist - edgePenalty + preferenceBias - avoidPenalty;
    if (score > bestScore) {
      bestScore = score;
      bestAnchor = cand;
    }
  }

  return bestAnchor;
}

/**
 * World-space position perpendicular to a two-point annotation, offset from
 * the segment midpoint by `distance` world units. Direction is picked so
 * the point lies above the segment in screen space (negative Y in world for
 * the standard viewer camera). Used to fix the hover chip on a normal from
 * the annotation midpoint — no auto-placement dance needed because at most
 * one chip is visible at a time.
 */
export function perpendicularLabelPosition(
  p0: CoreTypes.Point3 | undefined,
  p1: CoreTypes.Point3 | undefined,
  distance: number
): [number, number, number] {
  const [x0, y0] = p0 ?? [0, 0, 0];
  const [x1, y1] = p1 ?? [0, 0, 0];
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  if (len === 0) return [mx, my, 0];
  let perpX = -dy / len;
  let perpY = dx / len;
  if (perpY > 0) {
    perpX = -perpX;
    perpY = -perpY;
  }
  return [mx + perpX * distance, my + perpY * distance, 0];
}

/**
 * Read the user-moved flag set on `data.handles.textBox` when the user has
 * dragged the label manually. Returns true when the auto-placer should leave
 * the label alone.
 */
export function isLabelUserMoved(textBox: unknown): boolean {
  if (!textBox || typeof textBox !== 'object') return false;
  return (textBox as { userMoved?: boolean }).userMoved === true;
}

interface TextBoxLike {
  worldPosition?: number[];
  hasMoved?: boolean;
  userMoved?: boolean;
}

interface AnnotationLike {
  /** Used to give labels a stable priority so de-overlap can't oscillate. */
  annotationUID?: string;
  data: { handles: { textBox?: unknown } };
}

interface ViewportLike {
  element: HTMLElement;
  canvasToWorld: (
    p: CoreTypes.Point2 | [number, number]
  ) => CoreTypes.Point3 | number[];
  worldToCanvas?: (p: CoreTypes.Point3 | number[]) => CoreTypes.Point2;
}

/**
 * Canvas-space anchors of the OTHER spectral-Doppler labels on this element,
 * for the auto-placer to steer around. Only labels that sort *before* `selfUid`
 * are returned: that total order makes de-overlap a DAG (each label yields only
 * to higher-priority ones), so placement settles to a fixed point instead of
 * two labels endlessly swapping spots. Returns [] when the sibling read or any
 * conversion isn't available — placement then behaves exactly as before.
 */
function collectSiblingLabelAnchors(
  element: HTMLElement,
  selfUid: string | undefined,
  worldToCanvas: (p: CoreTypes.Point3 | number[]) => CoreTypes.Point2
): CoreTypes.Point2[] {
  const anchors: CoreTypes.Point2[] = [];
  Object.values(SPECTRAL_DOPPLER_TOOL_NAMES).forEach((toolName) => {
    let anns: unknown[] = [];
    try {
      anns = (csAnnotation.state.getAnnotations(
        toolName,
        element as unknown as HTMLDivElement
      ) ?? []) as unknown[];
    } catch {
      return;
    }
    anns.forEach((raw) => {
      const a = raw as {
        annotationUID?: string;
        data?: { handles?: { textBox?: { worldPosition?: number[] } } };
      };
      const uid = a.annotationUID;
      // Skip self and any label at or after us in the order (they yield to us).
      if (!uid || (selfUid !== undefined && uid >= selfUid)) return;
      const wp = a.data?.handles?.textBox?.worldPosition;
      if (!wp) return;
      const c = worldToCanvas([wp[0], wp[1], wp[2] ?? 0]);
      if (c && Number.isFinite(c[0]) && Number.isFinite(c[1]))
        anchors.push([c[0], c[1]]);
    });
  });
  return anchors;
}

/**
 * Place the annotation's text label automatically. Re-runs every render so the
 * anchor follows the geometry as it grows during drag, until the user drags
 * the label themselves — at which point `userMoved` flips on the textBox and
 * we leave it alone forever after.
 */
export function applySmartLabelAnchor(args: {
  annotation: AnnotationLike;
  geometryCanvasPoints: CoreTypes.Point2[];
  viewport: ViewportLike;
  options?: PickAnchorOptions;
}): void {
  const { annotation, geometryCanvasPoints, viewport, options } = args;

  const existing = annotation.data.handles.textBox as TextBoxLike | undefined;
  if (isLabelUserMoved(existing)) return;

  if (
    typeof viewport.canvasToWorld !== 'function' ||
    !viewport.element ||
    typeof viewport.element.getBoundingClientRect !== 'function'
  ) {
    return;
  }

  const rect = viewport.element.getBoundingClientRect();
  const avoidCanvasPoints =
    typeof viewport.worldToCanvas === 'function'
      ? collectSiblingLabelAnchors(
          viewport.element,
          annotation.annotationUID,
          viewport.worldToCanvas
        )
      : undefined;
  const canvasAnchor = pickLabelAnchor(
    geometryCanvasPoints,
    { width: rect.width, height: rect.height },
    { ...options, avoidCanvasPoints }
  );

  const worldAnchor = viewport.canvasToWorld(canvasAnchor) as number[];
  if (!worldAnchor || typeof worldAnchor[0] !== 'number') return;

  // Mutate in place rather than replacing the whole textBox: Cornerstone's
  // `getHandleNearImagePoint` reads `worldBoundingBox` (set by
  // `renderLinkedTextBoxAnnotation` after the first paint) to hit-test drags.
  // Replacing the object would drop that bounding box and break the textBox
  // drag handle on the next interaction.
  const tb = (existing ?? {}) as TextBoxLike & Record<string, unknown>;
  tb.worldPosition = [worldAnchor[0], worldAnchor[1], worldAnchor[2] ?? 0];
  tb.hasMoved = true;
  tb.userMoved = false;

  annotation.data.handles.textBox = tb;
}
